import React from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme, type ThemeMode } from "@/contexts/ThemeContext";
import { useTelegram } from "@/contexts/UnifiedAuthContext";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface ThemeToggleProps {
  className?: string;
}

/**
 * Компонент переключения тем для десктопа
 * Показывает три кнопки: Светлая | Темная | Автоматически
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className }) => {
  const { themeMode, setThemeMode, resolvedTheme } = useTheme();
  const { isTelegram } = useTelegram();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // Не показываем в Telegram или на мобильных устройствах
  if (isTelegram || isMobile) {
    return null;
  }

  const themes: Array<{
    mode: ThemeMode;
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    tooltip: string;
  }> = [
      {
        mode: 'light',
        icon: Sun,
        label: t('theme.light.label'),
        tooltip: t('theme.light.tooltip'),
      },
      {
        mode: 'dark',
        icon: Moon,
        label: t('theme.dark.label'),
        tooltip: t('theme.dark.tooltip'),
      },
      {
        mode: 'auto',
        icon: Monitor,
        label: t('theme.auto.label'),
        tooltip: t('theme.auto.tooltip'),
      },
    ];

  return (
    <div className={cn("flex items-center rounded-md border p-1", className)}>
      {themes.map(({ mode, icon: Icon, label, tooltip }) => {
        const isActive = themeMode === mode;

        return (
          <Button
            key={mode}
            variant={isActive ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setThemeMode(mode)}
            className={cn(
              "relative h-8 px-3 text-xs font-medium transition-all",
              isActive && "bg-accent text-accent-foreground shadow-sm"
            )}
            title={tooltip}
          >
            <Icon className="mr-2 h-3.5 w-3.5" />
            {label}
            {/* Индикатор текущего состояния */}
            {isActive && mode === 'auto' && (
              <span className="ml-1 text-xs opacity-60">
                ({resolvedTheme === 'dark' ? '🌙' : '☀️'})
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
};

/**
 * Компактная версия переключателя тем (только иконки)
 */
export const ThemeToggleCompact: React.FC<ThemeToggleProps> = ({ className }) => {
  const { themeMode, setThemeMode, resolvedTheme } = useTheme();
  const { isTelegram } = useTelegram();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // Не показываем в Telegram или на мобильных устройствах
  if (isTelegram || isMobile) {
    return null;
  }

  const themes: Array<{
    mode: ThemeMode;
    icon: React.ComponentType<{ className?: string }>;
    tooltip: string;
  }> = [
      {
        mode: 'light',
        icon: Sun,
        tooltip: t('theme.light.label_full'),
      },
      {
        mode: 'dark',
        icon: Moon,
        tooltip: t('theme.dark.label_full'),
      },
      {
        mode: 'auto',
        icon: Monitor,
        tooltip: t('theme.auto.desc_alt', {
          theme: resolvedTheme === 'dark' ? t('theme.resolved.dark') : t('theme.resolved.light')
        }),
      },
    ];

  return (
    <div className={cn("flex items-center rounded-md border", className)}>
      {themes.map(({ mode, icon: Icon, tooltip }) => {
        const isActive = themeMode === mode;

        return (
          <Button
            key={mode}
            variant={isActive ? "secondary" : "ghost"}
            size="icon"
            onClick={() => setThemeMode(mode)}
            className={cn(
              "h-8 w-8 rounded-none first:rounded-l-md last:rounded-r-md transition-all",
              isActive && "bg-accent text-accent-foreground shadow-sm"
            )}
            title={tooltip}
          >
            <Icon className="h-4 w-4" />
          </Button>
        );
      })}
    </div>
  );
};

export default ThemeToggle;